import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { GroupItemPlaceHolder } from '../group-item/GroupItemPlaceHolder';

const { number } = PropTypes;

const GroupIntersectionObserver = ({
  children, numberOfItemsLoaded, totalItemsInGroup, groupId, loadMore, placeHolderConfig,
  groupChangeStatus, groupIndex, disablePlaceholders
}) => {
  const { numberOfPlaceHolders: pageSize } = placeHolderConfig;
  let paginationObserver = useRef(null);
  const itemsWrapper = useRef(null);

  const disconnect = () => {
    if (paginationObserver.current?.disconnect) {
      paginationObserver.current.disconnect();
    }
  };

  useEffect(() => {
    return () => {
      disconnect();
    };
  }, []);

  const observe = (elem, cb) => {
    const observerOptions = {
      rootMargin: '0px 0px 300px 0px'
    };
    paginationObserver.current = new IntersectionObserver(async (_ref2, obs) => {
      let [entry] = _ref2;
      if (!entry.isIntersecting) return;
      disconnect();
      cb();
    }, observerOptions);
    paginationObserver.current.observe(elem);
  };

  const onIntersectionTrigger = () => {
    loadMore({ groupId });
  };

  useEffect(() => {
    let layoutChanged = false;
    groupChangeStatus.forEach((status, index) => {
      if (index < groupIndex && status) layoutChanged = true;
    });
    const attachLoader = loadMore && numberOfItemsLoaded > 0 && numberOfItemsLoaded < totalItemsInGroup;

    if ((layoutChanged && attachLoader) || attachLoader) {
      const loadedItems = itemsWrapper.current?.querySelectorAll('[data-testid="draggable-item"]');
      const totalItems = loadedItems?.length;
      if (totalItems > 0) {
        const lastChild = loadedItems[totalItems - 1];
        disconnect();
        observe(lastChild, onIntersectionTrigger);
      }
    }
  }, [numberOfItemsLoaded, totalItemsInGroup, groupChangeStatus]);

  const noItemsLoading = useMemo(() => {
    const _noItemsLoading = Math.min((totalItemsInGroup - numberOfItemsLoaded), pageSize);

    if (_noItemsLoading < 0) return 0;
    return _noItemsLoading;
  }, [totalItemsInGroup, numberOfItemsLoaded, pageSize]);

  if (!loadMore) return children;

  return (
    <>
      <div ref={itemsWrapper}>
        {children}
      </div>
      {
        !disablePlaceholders && [...Array(noItemsLoading).keys()].map((item) => (
          <GroupItemPlaceHolder placeHolderConfig={placeHolderConfig} key={`placeholder-${item}`} />
        ))
      }
    </>
  );
};

GroupIntersectionObserver.displayName = 'GroupIntersectionObserver';

GroupIntersectionObserver.propTypes = {
  groupId: PropTypes.string.isRequired,
  numberOfItemsLoaded: PropTypes.number,
  totalItemsInGroup: PropTypes.number,
  loadMore: PropTypes.func,
  children: PropTypes.node,
  placeHolderConfig: PropTypes.shape({
    height: PropTypes.number,
    numberOfLines: PropTypes.number,
    numberOfPlaceHolders: number
  }).isRequired,
  groupChangeStatus: PropTypes.arrayOf(PropTypes.bool).isRequired,
  groupIndex: PropTypes.number.isRequired,
  disablePlaceholders: PropTypes.bool
};

GroupIntersectionObserver.defaultProps = {
  children: null,
  numberOfItemsLoaded: null,
  totalItemsInGroup: null,
  loadMore: null,
  disablePlaceholders: false
};

export { GroupIntersectionObserver };
